import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash';
import { SerializedPresaveWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../common/types';
import { ConfirmDialog } from '../../../common/views/modals/confirmDialog/ConfirmDialog';
import { ConfirmDialogIconMark } from '../../../common/views/modals/confirmDialog/ConfirmDialogMark';
import { useGetPresaveCampaignQuery } from '../../../../redux/api/fymAPI';
import { blockMessages as m } from './blockMessages';

interface SMLConfirmCatalogDeleteProps {
  accountId: number;
  widget: SerializedPresaveWidget;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The one delete that asks first (design d10): Catalog links with an open pre-save that has fans.
 * With no fan yet the delete goes on at once, with Undo like every other block.
 */
const SMLConfirmCatalogDeleteComponent: React.FC<SMLConfirmCatalogDeleteProps> = ({
  accountId,
  widget,
  onConfirm,
  onCancel,
}) => {
  const intl = useIntl();
  const { data, isLoading, isError } = useGetPresaveCampaignQuery({
    accountId,
    campaignId: widget.data.campaignUuid,
  });
  const fans = useMemo(() => {
    if (data?.stats === undefined) {
      return null;
    }
    const services = data.stats.byService.reduce((sum, row) => sum + row.count, 0);
    return services + data.stats.emailCount;
  }, [data]);

  useEffect(() => {
    // No fan to lose (or no count to show): no question.
    if (!isLoading && (fans === 0 || isError)) {
      onConfirm();
    }
  }, [fans, isError, isLoading, onConfirm]);

  if (fans === null || fans === 0) {
    return null;
  }

  return (
    <ConfirmDialog
      isOpen
      tone="danger"
      mark={<ConfirmDialogIconMark tone="danger" icon={faTrash} />}
      title={intl.formatMessage(m.confirmDeleteTitle)}
      description={intl.formatMessage(m.confirmDeleteBody, { count: fans, title: widget.data.trackTitle })}
      confirmLabel={intl.formatMessage(m.confirmDeleteConfirm)}
      onConfirm={onConfirm}
      onCancel={onCancel}
      testId="catalog-delete-dialog"
    />
  );
};

export const SMLConfirmCatalogDelete = TypedMemo(SMLConfirmCatalogDeleteComponent);
